import React from 'react';
import { Modal } from 'react-bootstrap';

/**
 * CategoryConfirmationModal - Reusable modal for displaying categorized items with tooltips
 *
 * @param {boolean} show - Controls modal visibility
 * @param {function} onHide - Callback when modal is closed
 * @param {function} onConfirm - Callback when user confirms action
 * @param {object} categoryData - Object containing category data with structure:
 *   {
 *     categoryKey: { count: number, items: array },
 *     total: number
 *   }
 * @param {array} categoryConfig - Array of category configurations:
 *   [{ key: string, label: string }]
 * @param {string} hoveredCategory - Current hovered category key
 * @param {function} setHoveredCategory - Setter for hovered category
 * @param {object} tooltipPosition - Current tooltip position { x, y }
 * @param {function} setTooltipPosition - Setter for tooltip position
 * @param {function} handleTooltipMouseMove - Throttled mousemove handler
 * @param {string} title - Modal title (e.g., "Confirm Deletion")
 * @param {string} message - Message to display above categories
 * @param {string} confirmText - Text for confirm button
 * @param {function} renderTooltipContent - Function to render tooltip content for a category
 *   Signature: (category, categoryData) => JSX
 * @param {object} styles - Style objects for modal components
 */
const CategoryConfirmationModal = ({
  show,
  onHide,
  onConfirm,
  categoryData,
  categoryConfig,
  hoveredCategory,
  setHoveredCategory,
  tooltipPosition,
  setTooltipPosition,
  handleTooltipMouseMove,
  title,
  message,
  confirmText = 'Confirm',
  renderTooltipContent,
  styles,
}) => {
  return (
    <>
      {/* Category Confirmation Modal */}
      <Modal show={show} onHide={onHide}>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={styles.modalBodyText}>{message}</p>
          <div style={styles.categoryContainer}>
            {categoryData &&
              categoryConfig.map(
                (category) =>
                  categoryData[category.key]?.count > 0 && (
                    <div
                      key={category.key}
                      style={{
                        ...styles.categoryRow,
                        ...(hoveredCategory === category.key
                          ? styles.categoryRowHover
                          : {}),
                      }}
                      onMouseEnter={(e) => {
                        setHoveredCategory(category.key);
                        setTooltipPosition({ x: e.clientX, y: e.clientY });
                      }}
                      onMouseMove={handleTooltipMouseMove}
                      onMouseLeave={() => setHoveredCategory(null)}
                    >
                      <span style={styles.categoryLabel}>{category.label}</span>
                      <span style={styles.categoryCount}>
                        {categoryData[category.key].count}
                      </span>
                    </div>
                  ),
              )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-default" onClick={onHide}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm}>
            {confirmText}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Tooltip for category details on hover */}
      {hoveredCategory && categoryData && renderTooltipContent && (
        <div
          style={{
            ...styles.tooltip,
            left: `${tooltipPosition.x + 15}px`,
            top: `${tooltipPosition.y + 15}px`,
          }}
        >
          {renderTooltipContent(hoveredCategory, categoryData)}
        </div>
      )}
    </>
  );
};

export default CategoryConfirmationModal;
